// src/Views/Support/Support.js
// 🔥 FIXED: Solid backgrounds to prevent bleed-through on notifications & menus

import React, { useState, useEffect } from 'react';
import { db } from '../../firebase'; 
import { useAuth } from '../../context/AuthContext';
import { 
    collection, addDoc, doc, updateDoc, query, where, orderBy, onSnapshot, serverTimestamp, arrayUnion, Timestamp
} from 'firebase/firestore';
import { Icon } from '@iconify/react';
import './Support.css';

const Support = () => {
    const { user } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [subject, setSubject] = useState('General Inquiry');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    
    // For viewing ticket conversation
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [sendingReply, setSendingReply] = useState(false);

    // 1. Fetch My Tickets
    useEffect(() => {
        if (!user) return;

        const q = query(
            collection(db, 'tickets'),
            where('uid', '==', user.uid),
            orderBy('createdAt', 'desc')
        );

        const unsub = onSnapshot(q, (snap) => {
            const ticketData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setTickets(ticketData);
            
            // Update selected ticket if it's open
            if (selectedTicket) {
                const updated = ticketData.find(t => t.id === selectedTicket.id);
                if (updated) setSelectedTicket(updated);
            }
        });

        return () => unsub();
    }, [user, selectedTicket?.id]);

    // 2. Submit New Ticket
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;

        setLoading(true);
        try {
            await addDoc(collection(db, 'tickets'), {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName || 'User',
                subject,
                status: 'open',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                messages: [
                    {
                        from: 'user',
                        name: user.displayName || 'You',
                        text: message,
                        timestamp: Timestamp.now()
                    }
                ],
                unreadByAdmin: true,
                unreadByUser: false
            });
            setSuccess('Ticket created! Our team will respond shortly.');
            setMessage('');
            setTimeout(() => setSuccess(''), 4000);
        } catch (error) {
            console.error("Ticket Error:", error);
            alert("Failed to submit ticket.");
        } finally {
            setLoading(false);
        }
    };

    // 3. Send Follow-up Message
    const handleSendReply = async () => {
        if (!replyMessage.trim() || !selectedTicket) return;

        setSendingReply(true);
        try {
            const ticketRef = doc(db, 'tickets', selectedTicket.id);
            await updateDoc(ticketRef, {
                messages: arrayUnion({
                    from: 'user',
                    name: user.displayName || 'You',
                    text: replyMessage,
                    timestamp: Timestamp.now()
                }),
                updatedAt: serverTimestamp(),
                unreadByAdmin: true,
                status: selectedTicket.status === 'resolved' ? 'open' : selectedTicket.status
            });
            setReplyMessage('');
        } catch (error) {
            console.error("Reply Error:", error);
            alert("Failed to send message.");
        } finally {
            setSendingReply(false);
        }
    };

    // 4. Mark ticket as read by user
    const handleOpenTicket = async (ticket) => {
        setSelectedTicket(ticket);
        
        if (ticket.unreadByUser) {
            try {
                const ticketRef = doc(db, 'tickets', ticket.id);
                await updateDoc(ticketRef, {
                    unreadByUser: false
                });
            } catch (error) {
                console.error("Mark read error:", error);
            }
        }
    };

    // Format timestamp
    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        
        if (diff < 86400000) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        if (diff < 604800000) {
            return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    // Status badge color
    const getStatusStyle = (status) => {
        switch (status) {
            case 'open':
                return { background: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' };
            case 'in_progress':
                return { background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)' };
            case 'resolved':
                return { background: 'rgba(74, 222, 128, 0.15)', color: '#4ade80', border: '1px solid rgba(74, 222, 128, 0.3)' };
            default:
                return { background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8', border: '1px solid rgba(148, 163, 184, 0.3)' };
        }
    };

    const formatStatus = (status) => {
        switch (status) {
            case 'in_progress': return 'In Progress';
            case 'resolved': return 'Resolved';
            default: return 'Open';
        }
    };

    return (
        <div className="support" style={{ 
            padding: '20px', 
            maxWidth: '900px', 
            margin: '0 auto', 
            color: '#fff',
            minHeight: '100vh',
            /* 🔥 FIX: Ensure Support page is below overlays */
            position: 'relative',
            zIndex: 1
        }}>
            
            {/* HEADER */}
            <header style={{ marginBottom: '32px', textAlign: 'center' }}>
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    /* 🔥 FIX: Solid background instead of transparent */
                    background: '#1e3a5f',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    marginBottom: '16px',
                    border: '1px solid rgba(59, 130, 246, 0.3)'
                }}>
                    <Icon icon="solar:headphones-round-sound-bold" style={{ color: '#60a5fa' }} />
                    <span style={{ color: '#60a5fa', fontSize: '0.85rem', fontWeight: '500' }}>Support Center</span>
                </div>
                <h1 style={{ fontSize: '1.75rem', marginBottom: '8px' }}>How can we help?</h1>
                <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
                    Whether it's bot setup, API keys, predictions, or payments — our team typically responds within a few hours.
                </p>
            </header>

            {/* MAIN CONTENT - Two Column Layout */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: selectedTicket ? '1fr 1.5fr' : '1fr', 
                gap: '20px',
                transition: 'all 0.3s ease'
            }}>
                
                {/* LEFT: Ticket List + New Ticket Form */}
                <div>
                    {/* NEW TICKET FORM */}
                    <div style={{ 
                        /* 🔥 FIX: Solid background instead of gradient with transparency */
                        background: '#1e293b',
                        padding: '20px', 
                        borderRadius: '16px', 
                        marginBottom: '20px',
                        border: '1px solid rgba(71, 85, 105, 0.3)'
                    }}>
                        <h3 style={{ marginBottom: '16px', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Icon icon="solar:pen-new-square-bold" style={{ color: '#60a5fa' }} />
                            New Ticket
                        </h3>
                        
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#94a3b8' }}>Topic</label>
                                <select 
                                    value={subject} 
                                    onChange={(e) => setSubject(e.target.value)}
                                    style={{ 
                                        width: '100%', 
                                        padding: '12px', 
                                        borderRadius: '10px', 
                                        /* 🔥 FIX: Solid background */
                                        background: '#0f172a', 
                                        border: '1px solid rgba(71, 85, 105, 0.5)', 
                                        color: '#fff',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    <option>General Inquiry</option>
                                    <option>Crypto AI Bot — Setup Help</option>
                                    <option>Crypto AI Bot — API Key Issue</option>
                                    <option>Crypto AI Bot — Trade Issue</option>
                                    <option>Crypto AI Bot — Payment / USDT</option>
                                    <option>Crypto AI Bot — Tier Change</option>
                                    <option>Billing Issue</option>
                                    <option>Bug Report</option>
                                    <option>Feature Request</option>
                                    <option>Account Issue</option>
                                    <option>Prediction Question</option>
                                </select>
                            </div>

                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#94a3b8' }}>Message</label>
                                <textarea 
                                    rows="3"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Describe your issue or question..."
                                    style={{ 
                                        width: '100%', 
                                        padding: '12px', 
                                        borderRadius: '10px', 
                                        /* 🔥 FIX: Solid background */
                                        background: '#0f172a', 
                                        border: '1px solid rgba(71, 85, 105, 0.5)', 
                                        color: '#fff',
                                        fontSize: '0.95rem',
                                        resize: 'vertical',
                                        minHeight: '80px'
                                    }}
                                />
                            </div>

                            <button 
                                type="submit" 
                                disabled={loading || !message.trim()}
                                style={{ 
                                    background: loading ? '#475569' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                    color: '#fff', 
                                    padding: '12px 20px', 
                                    borderRadius: '10px', 
                                    fontWeight: '600', 
                                    border: 'none', 
                                    cursor: loading ? 'not-allowed' : 'pointer', 
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    fontSize: '0.95rem',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <Icon icon={loading ? "solar:spinner-bold" : "solar:plain-bold"} 
                                    style={{ fontSize: '1.1rem', animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                                {loading ? 'Sending...' : 'Submit Ticket'}
                            </button>

                            {success && (
                                <p style={{ 
                                    color: '#4ade80', 
                                    marginTop: '12px', 
                                    textAlign: 'center',
                                    fontSize: '0.9rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}>
                                    <Icon icon="solar:check-circle-bold" />
                                    {success}
                                </p>
                            )}
                        </form>
                    </div>

                    {/* TICKET LIST */}
                    <div>
                        <h3 style={{ 
                            marginBottom: '12px', 
                            fontSize: '1rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            color: '#e2e8f0'
                        }}>
                            <Icon icon="solar:inbox-bold" style={{ color: '#94a3b8' }} />
                            My Tickets ({tickets.length})
                        </h3>
                        
                        {tickets.length === 0 && (
                            <div style={{ 
                                /* 🔥 FIX: Solid background */
                                background: '#1e293b',
                                padding: '24px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '1px solid rgba(71, 85, 105, 0.3)'
                            }}>
                                <Icon icon="solar:inbox-line-bold" style={{ fontSize: '2rem', color: '#475569', marginBottom: '8px' }} />
                                <p style={{ color: '#64748b', fontSize: '0.9rem' }}>No tickets yet</p>
                            </div>
                        )}
                        
                        {tickets.map(ticket => (
                            <div 
                                key={ticket.id} 
                                onClick={() => handleOpenTicket(ticket)}
                                style={{ 
                                    /* 🔥 FIX: Solid backgrounds */
                                    background: selectedTicket?.id === ticket.id 
                                        ? '#1e3a5f' 
                                        : '#1e293b',
                                    padding: '14px 16px', 
                                    borderRadius: '12px', 
                                    marginBottom: '10px', 
                                    border: selectedTicket?.id === ticket.id 
                                        ? '1px solid rgba(59, 130, 246, 0.4)'
                                        : '1px solid rgba(71, 85, 105, 0.3)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontWeight: '600', color: '#e2e8f0', fontSize: '0.95rem' }}>
                                            {ticket.subject}
                                        </span>
                                        {ticket.unreadByUser && (
                                            <span style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                background: '#3b82f6',
                                                display: 'inline-block'
                                            }} title="New reply" />
                                        )}
                                    </div>
                                    <span style={{ 
                                        padding: '3px 10px', 
                                        borderRadius: '12px', 
                                        fontSize: '0.7rem',
                                        fontWeight: '600',
                                        ...getStatusStyle(ticket.status)
                                    }}>
                                        {formatStatus(ticket.status)}
                                    </span>
                                </div>
                                <p style={{ 
                                    color: '#94a3b8', 
                                    fontSize: '0.85rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    marginBottom: '6px'
                                }}>
                                    {ticket.messages?.[ticket.messages.length - 1]?.text || 'No messages'}
                                </p>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <small style={{ color: '#475569', fontSize: '0.75rem' }}>
                                        {ticket.messages?.length || 0} message{ticket.messages?.length !== 1 ? 's' : ''}
                                    </small>
                                    <small style={{ color: '#475569', fontSize: '0.75rem' }}>
                                        {formatTime(ticket.updatedAt || ticket.createdAt)}
                                    </small>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* RIGHT: Conversation Thread */}
                {selectedTicket && (
                    <div style={{
                        /* 🔥 FIX: Solid background */
                        background: '#1e293b',
                        borderRadius: '16px',
                        border: '1px solid rgba(71, 85, 105, 0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'fit-content',
                        maxHeight: '600px'
                    }}>
                        {/* Thread Header */}
                        <div style={{
                            padding: '16px 20px',
                            borderBottom: '1px solid rgba(71, 85, 105, 0.3)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            /* 🔥 FIX: Solid background */
                            background: '#0f172a',
                            borderRadius: '16px 16px 0 0'
                        }}>
                            <div>
                                <h3 style={{ fontSize: '1rem', marginBottom: '4px', color: '#e2e8f0' }}>
                                    {selectedTicket.subject}
                                </h3>
                                <span style={{ 
                                    padding: '3px 10px', 
                                    borderRadius: '12px', 
                                    fontSize: '0.7rem',
                                    fontWeight: '600',
                                    ...getStatusStyle(selectedTicket.status)
                                }}>
                                    {formatStatus(selectedTicket.status)}
                                </span>
                            </div>
                            <button 
                                onClick={() => setSelectedTicket(null)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#64748b',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Icon icon="solar:close-circle-bold" style={{ fontSize: '1.5rem' }} />
                            </button>
                        </div>

                        {/* Messages */}
                        <div style={{
                            padding: '16px 20px',
                            flex: 1,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            maxHeight: '350px',
                            /* 🔥 FIX: Solid background */
                            background: '#1e293b'
                        }}>
                            {selectedTicket.messages?.map((msg, idx) => (
                                <div 
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: msg.from === 'user' ? 'flex-end' : 'flex-start'
                                    }}
                                >
                                    <div style={{
                                        maxWidth: '85%',
                                        padding: '12px 16px',
                                        borderRadius: msg.from === 'user' 
                                            ? '16px 16px 4px 16px' 
                                            : '16px 16px 16px 4px',
                                        background: msg.from === 'user'
                                            ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                                            : '#334155',
                                        color: '#fff'
                                    }}>
                                        <p style={{ fontSize: '0.95rem', lineHeight: '1.5', margin: 0 }}>
                                            {msg.text}
                                        </p>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        marginTop: '4px',
                                        padding: '0 4px'
                                    }}>
                                        {msg.from === 'admin' && (
                                            <span style={{
                                                fontSize: '0.7rem',
                                                color: '#4ade80',
                                                fontWeight: '600'
                                            }}>
                                                AiPredict Team
                                            </span>
                                        )}
                                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                                            {formatTime(msg.timestamp)}
                                        </span>
                                    </div>
                                </div>
                            ))}

                            {/* Awaiting Response */}
                            {selectedTicket.status !== 'resolved' && 
                             selectedTicket.messages?.[selectedTicket.messages.length - 1]?.from === 'user' && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '12px',
                                    color: '#64748b',
                                    fontSize: '0.8rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}>
                                    <Icon icon="solar:clock-circle-bold" />
                                    Awaiting team response...
                                </div>
                            )}
                        </div>

                        {/* Reply Input */}
                        <div style={{
                            padding: '16px 20px',
                            borderTop: '1px solid rgba(71, 85, 105, 0.3)',
                            /* 🔥 FIX: Solid background */
                            background: '#0f172a',
                            borderRadius: '0 0 16px 16px'
                        }}>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <input
                                    type="text"
                                    value={replyMessage}
                                    onChange={(e) => setReplyMessage(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
                                    placeholder={selectedTicket.status === 'resolved' 
                                        ? "Reopen ticket by sending a message..." 
                                        : "Type your message..."}
                                    style={{
                                        flex: 1,
                                        padding: '12px 16px',
                                        borderRadius: '10px',
                                        /* 🔥 FIX: Solid background */
                                        background: '#1e293b',
                                        border: '1px solid rgba(71, 85, 105, 0.5)',
                                        color: '#fff',
                                        fontSize: '0.95rem'
                                    }}
                                />
                                <button
                                    onClick={handleSendReply}
                                    disabled={sendingReply || !replyMessage.trim()}
                                    style={{
                                        padding: '12px 16px',
                                        borderRadius: '10px',
                                        background: sendingReply || !replyMessage.trim() 
                                            ? '#475569' 
                                            : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                        border: 'none',
                                        color: '#fff',
                                        cursor: sendingReply || !replyMessage.trim() ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Icon icon={sendingReply ? "solar:spinner-bold" : "solar:plain-bold"} 
                                        style={{ fontSize: '1.2rem', animation: sendingReply ? 'spin 1s linear infinite' : 'none' }} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                @media (max-width: 768px) {
                    .support > div > div {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default Support;